import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, FileText, Settings, LogOut, BarChart3, Tag, Globe,
  Plus, Edit2, Trash2, Gift, Percent, RefreshCcw, Calendar, 
  TrendingUp, Users, ShoppingBag, Save, X, Check, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMarketing = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({});
  const [editModal, setEditModal] = useState({ open: false, type: null, data: null });
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, tradeInsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/campaigns`),
        axios.get(`${API_URL}/api/admin/tradein/requests`, { withCredentials: true }).catch(() => ({ data: { requests: [] } })),
        axios.get(`${API_URL}/api/admin/marketing/stats`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setTradeIns(tradeInsRes.data.requests || []);
      setStats(statsRes.data || {});
      
      // Fetch coupons from campaigns
      const allCoupons = (campaignsRes.data.campaigns || []).map(c => ({
        code: c.discount_code,
        campaign: c.name,
        discount: c.discount_percent,
        active: isDateActive(c.start_date, c.end_date),
        start: c.start_date,
        end: c.end_date,
        uses: c.uses || 0
      }));
      setCoupons(allCoupons);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDateActive = (start, end) => {
    const now = new Date().toISOString().split('T')[0];
    return start <= now && now <= end;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEditModal = (type, data = null) => {
    setFormData(data || getDefaultForm(type));
    setEditModal({ open: true, type, data });
  };

  const getDefaultForm = (type) => {
    switch (type) {
      case 'campaign':
        return { 
          name: '', slug: '', description: '', discount_percent: 10, discount_code: '',
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          applies_to: ['repairs'], min_order: 0, max_discount: 100,
          banner_color: '#0047FF', banner_text: '', terms: []
        };
      case 'coupon':
        return { code: '', discount_percent: 10, valid_until: '', max_uses: 100, min_order: 0 };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    try {
      const { type, data } = editModal;
      
      if (type === 'campaign') {
        if (data) {
          await axios.put(`${API_URL}/api/admin/campaigns/${formData.id}`, formData, { withCredentials: true });
        } else {
          await axios.post(`${API_URL}/api/admin/campaigns`, formData, { withCredentials: true });
        }
        toast.success('Campaña guardada');
      }
      
      setEditModal({ open: false, type: null, data: null });
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleTradeInStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/tradein/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Trade-In ${status === 'approved' ? 'aprobado' : 'rechazado'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-marketing">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Facturas</span>
          </Link>
          <Link to="/admin/seo" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Globe className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">SEO & Contenido</span>
          </Link>
          <Link to="/admin/marketing" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <Tag className="w-5 h-5" />
            <span className="font-medium">Marketing</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Settings className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Marketing & Promociones</h1>
              <p className="text-[#52525B]">Gestiona campañas, cupones y programa Trade-In</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-500">Campañas Activas</span>
              </div>
              <p className="text-2xl font-bold">{campaigns.filter(c => isDateActive(c.start_date, c.end_date)).length}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Percent className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">Cupones Usados</span>
              </div>
              <p className="text-2xl font-bold">{stats.coupons_used || 0}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <RefreshCcw className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-500">Trade-Ins Pendientes</span>
              </div>
              <p className="text-2xl font-bold">{tradeIns.filter(t => t.status === 'pending').length}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Gift className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm text-gray-500">Descuentos Otorgados</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_discounts || '0€'}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'campaigns', label: 'Campañas', icon: Calendar },
              { id: 'coupons', label: 'Cupones', icon: Percent },
              { id: 'tradein', label: 'Trade-In', icon: RefreshCcw }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#0047FF] text-white' 
                    : 'bg-white text-[#52525B] hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <>
              {/* Campaigns Tab */}
              {activeTab === 'campaigns' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button className="bg-[#0047FF] hover:bg-[#003BCC]" onClick={() => openEditModal('campaign')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Campaña
                    </Button>
                  </div>
                  
                  <div className="grid gap-4">
                    {campaigns.map((campaign, idx) => {
                      const isActive = isDateActive(campaign.start_date, campaign.end_date);
                      return (
                        <div 
                          key={idx} 
                          className={`bg-white rounded-xl border p-6 ${!isActive ? 'opacity-60' : ''}`}
                          style={{ borderLeftColor: campaign.banner_color, borderLeftWidth: 4 }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg">{campaign.name}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {isActive ? 'Activa' : 'Inactiva'}
                                </span>
                              </div>
                              <p className="text-gray-500 text-sm mb-3">{campaign.description}</p>
                              <div className="flex gap-4 text-sm">
                                <span className="flex items-center gap-1">
                                  <Percent className="w-4 h-4 text-blue-500" />
                                  <strong>{campaign.discount_percent}%</strong> descuento
                                </span>
                                <span className="flex items-center gap-1">
                                  <Tag className="w-4 h-4 text-purple-500" />
                                  Código: <code className="bg-gray-100 px-1 rounded">{campaign.discount_code}</code>
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-gray-500" />
                                  {campaign.start_date} → {campaign.end_date}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openEditModal('campaign', campaign)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {campaign.terms && campaign.terms.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-gray-500">Términos: {campaign.terms.join(' | ')}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Coupons Tab */}
              {activeTab === 'coupons' && (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Código</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Campaña</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Descuento</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Vigencia</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Estado</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Usos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {coupons.map((coupon, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <code className="bg-gray-100 px-2 py-1 rounded font-mono">{coupon.code}</code>
                          </td>
                          <td className="px-6 py-4 text-sm">{coupon.campaign}</td>
                          <td className="px-6 py-4">
                            <span className="text-green-600 font-medium">{coupon.discount}%</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {coupon.start} → {coupon.end}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              coupon.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {coupon.active ? 'Activo' : 'Expirado'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">{coupon.uses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Trade-In Tab */}
              {activeTab === 'tradein' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-800 mb-1">Programa Trade-In</h4>
                    <p className="text-sm text-blue-600">
                      Los clientes pueden entregar su dispositivo antiguo y recibir un descuento en reparaciones futuras.
                      Revisa las solicitudes pendientes y aprueba o rechaza según el estado del dispositivo.
                    </p>
                  </div>

                  {tradeIns.length === 0 ? (
                    <div className="bg-white rounded-xl border p-12 text-center">
                      <RefreshCcw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-medium text-gray-600 mb-1">No hay solicitudes de Trade-In</h3>
                      <p className="text-sm text-gray-400">Las solicitudes de los clientes aparecerán aquí</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Dispositivo</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Cliente</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Valor Estimado</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Estado</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Fecha</th>
                            <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {tradeIns.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-medium">{item.device_model}</p>
                                  <p className="text-sm text-gray-500">{item.brand}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm">{item.customer_email}</td>
                              <td className="px-6 py-4">
                                <span className="text-green-600 font-medium">{item.estimated_value}€</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  item.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {item.status === 'pending' ? 'Pendiente' : 
                                   item.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {new Date(item.created_at).toLocaleDateString('es-ES')}
                              </td>
                              <td className="px-6 py-4 text-right">
                                {item.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="text-green-600 mr-2"
                                      onClick={() => handleTradeInStatus(item.id, 'approved')}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="text-red-600"
                                      onClick={() => handleTradeInStatus(item.id, 'rejected')}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {editModal.open && editModal.type === 'campaign' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editModal.data ? 'Editar' : 'Nueva'} Campaña
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: false, type: null, data: null })}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre de la Campaña</Label>
                  <Input 
                    value={formData.name || ''} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Black Friday Gaming"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input 
                    value={formData.slug || ''} 
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="black-friday"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Descripción</Label>
                <Input 
                  value={formData.description || ''} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="25% de descuento en todas las reparaciones"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>% Descuento</Label>
                  <Input 
                    type="number"
                    value={formData.discount_percent || 10} 
                    onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
                    min={1}
                    max={100}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Código de Descuento</Label>
                  <Input 
                    value={formData.discount_code || ''} 
                    onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })}
                    placeholder="BLACKFRIDAY25"
                    className="mt-1 uppercase"
                  />
                </div>
                <div>
                  <Label>Descuento Máximo (€)</Label>
                  <Input 
                    type="number"
                    value={formData.max_discount || 100} 
                    onChange={(e) => setFormData({ ...formData, max_discount: parseInt(e.target.value) })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input 
                    type="date"
                    value={formData.start_date || ''} 
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Fecha Fin</Label>
                  <Input 
                    type="date"
                    value={formData.end_date || ''} 
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Color del Banner</Label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      type="color"
                      value={formData.banner_color || '#0047FF'} 
                      onChange={(e) => setFormData({ ...formData, banner_color: e.target.value })}
                      className="w-16 h-10 p-1"
                    />
                    <Input 
                      value={formData.banner_color || '#0047FF'} 
                      onChange={(e) => setFormData({ ...formData, banner_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Texto del Banner</Label>
                  <Input 
                    value={formData.banner_text || ''} 
                    onChange={(e) => setFormData({ ...formData, banner_text: e.target.value })}
                    placeholder="BLACK FRIDAY: 25% EN TODO"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Términos y Condiciones (uno por línea)</Label>
                <textarea 
                  value={Array.isArray(formData.terms) ? formData.terms.join('\n') : ''} 
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value.split('\n').filter(t => t.trim()) })}
                  placeholder="Válido del 25 Nov al 2 Dic&#10;No acumulable con otras ofertas"
                  className="mt-1 w-full border rounded-md px-3 py-2 h-24"
                />
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditModal({ open: false, type: null, data: null })}>
                Cancelar
              </Button>
              <Button className="bg-[#0047FF] hover:bg-[#003BCC]" onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Guardar Campaña
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMarketing;
